import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import { FaPlus, FaEdit, FaTrash, FaFilePdf, FaFileWord, FaFileExcel, FaFileImage, FaDownload, FaEye } from 'react-icons/fa';
import { apiService } from '../services/api';
import { normalizeListForAdmin, toBool } from '../utils/normalizeApi';
import { readValidatedDocumentAsDataUrl, validateDocumentFile } from '../utils/imageUpload';
import { resolvePublicMediaUrl } from '../utils/mediaUrl';

function DownloadsManagement() {
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    fileUrl: '',
    fileType: 'PDF',
    category: 'General',
    isActive: true
  });

  const [fileName, setFileName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  const categories = ['General', 'Academic', 'Exam', 'Sports', 'Events', 'Circulars', 'Results'];
  const fileTypes = [
    { value: 'PDF', label: 'PDF Document', icon: FaFilePdf, color: 'text-red-600' },
    { value: 'DOC', label: 'Word Document', icon: FaFileWord, color: 'text-blue-600' },
    { value: 'DOCX', label: 'Word Document', icon: FaFileWord, color: 'text-blue-600' },
    { value: 'XLS', label: 'Excel Sheet', icon: FaFileExcel, color: 'text-green-600' },
    { value: 'XLSX', label: 'Excel Sheet', icon: FaFileExcel, color: 'text-green-600' },
    { value: 'JPG', label: 'Image', icon: FaFileImage, color: 'text-purple-600' },
    { value: 'PNG', label: 'Image', icon: FaFileImage, color: 'text-purple-600' }
  ];

  useEffect(() => { fetchDownloads(); }, []);

  const fetchDownloads = async () => {
    try {
      const response = await apiService.downloads.getAllAdmin();
      setDownloads(normalizeListForAdmin(response.data));
    } catch (error) {
      console.error('Error fetching downloads:', error);
      setDownloads([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert('Title is required');
      return;
    }
    if (!editingId && !formData.fileUrl.trim()) {
      alert('Upload a file or paste a file URL');
      return;
    }
    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description,
        fileUrl: formData.fileUrl,
        fileType: formData.fileType,
        category: formData.category,
        isActive: formData.isActive,
      };

      let res;
      if (editingId) {
        res = await apiService.downloads.update(editingId, payload);
        if (res.data?.success === false) {
          throw new Error(res.data?.message || 'Update failed');
        }
        alert('✅ Download updated successfully!');
      } else {
        res = await apiService.downloads.create(payload);
        if (res.data?.success === false) {
          throw new Error(res.data?.message || 'Create failed');
        }
        alert('✅ Download added successfully!');
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      fetchDownloads();
    } catch (error) {
      console.error('Download CRUD Error:', error);
      const errorMsg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Unknown error';
      alert('❌ Error: ' + errorMsg);
    }
  };

  const handleEdit = (item) => {
    console.log('📝 Editing download:', item);
    setEditingId(item.id);
    setFormData({
      title: item.title || '',
      description: item.description || '',
      fileUrl: item.fileUrl || '',
      fileType: item.fileType || 'PDF',
      category: item.category || 'General',
      isActive: toBool(item.isActive, true)
    });
    setFileName('');
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this download?')) return;
    try {
      await apiService.downloads.delete(id);
      alert('✅ Download deleted successfully!');
      fetchDownloads();
    } catch (error) {
      console.error('Delete error:', error);
      alert('❌ Error deleting download: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateDocumentFile(file);
    if (err) {
      alert(err);
      e.target.value = '';
      return;
    }
    setFileName(file.name);
    setSelectedFile(file);
    try {
      const dataUrl = await readValidatedDocumentAsDataUrl(file);
      const ext = file.name.split('.').pop()?.toUpperCase() || 'PDF';
      const typeMap = { PDF: 'PDF', DOC: 'DOC', DOCX: 'DOCX', XLS: 'XLS', XLSX: 'XLSX', JPG: 'JPG', JPEG: 'JPG', PNG: 'PNG' };
      setFormData((prev) => ({
        ...prev,
        fileUrl: dataUrl,
        fileType: typeMap[ext] || prev.fileType,
      }));
    } catch (readErr) {
      alert(readErr.message || 'Could not read file');
      e.target.value = '';
      setFileName('');
      setSelectedFile(null);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', fileUrl: '', fileType: 'PDF', category: 'General', isActive: true });
    setFileName('');
    setSelectedFile(null);
  };

  const getFileIcon = (type) => {
    const fileType = fileTypes.find(ft => ft.value === type);
    if (fileType) {
      const Icon = fileType.icon;
      return <Icon className={`text-3xl ${fileType.color}`} />;
    }
    return <FaFilePdf className="text-3xl text-gray-600" />;
  };

  if (loading) return <AdminLayout><div className="p-6">Loading...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-6">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-blue-900">Downloads Management</h1>
            <p className="text-gray-600 mt-1">Manage downloadable resources for students & parents</p>
          </div>
          <button onClick={() => setShowForm(true)}
            className="bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition flex items-center gap-2 shadow-lg">
            <FaPlus /> Add Download
          </button>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Files</p>
                <p className="text-3xl font-bold text-blue-900">{downloads.length}</p>
              </div>
              <div className="bg-blue-100 p-4 rounded-full">
                <FaDownload className="text-2xl text-blue-900" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Downloads</p>
                <p className="text-3xl font-bold text-green-900">{downloads.reduce((sum, d) => sum + (d.downloadCount || 0), 0)}</p>
              </div>
              <div className="bg-green-100 p-4 rounded-full">
                <FaEye className="text-2xl text-green-900" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Files</p>
                <p className="text-3xl font-bold text-purple-900">{downloads.filter(d => d.isActive).length}</p>
              </div>
              <div className="bg-purple-100 p-4 rounded-full">
                <FaFilePdf className="text-2xl text-purple-900" />
              </div>
            </div>
          </div>
        </div>

        {/* Downloads Grid */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {downloads.map((item) => (
            <div key={item.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-blue-900 mb-1">{item.title}</h3>
                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      {item.category}
                    </span>
                  </div>
                  {getFileIcon(item.fileType)}
                </div>
                
                {item.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{item.description}</p>
                )}
                
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <span>📊 {item.downloadCount || 0} downloads</span>
                  <span className={`px-2 py-1 rounded text-xs ${item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {item.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="flex gap-2 pt-3 border-t">
                  {item.fileUrl && (
                    <>
                      <a
                        href={resolvePublicMediaUrl(item.fileUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-green-50 text-green-900 py-2 rounded-lg hover:bg-green-100 transition text-center text-sm font-medium flex items-center justify-center gap-2"
                      >
                        <FaEye size={14} /> View
                      </a>
                      <a
                        href={resolvePublicMediaUrl(item.fileUrl)}
                        download
                        className="flex-1 bg-blue-50 text-blue-900 py-2 rounded-lg hover:bg-blue-100 transition text-center text-sm font-medium flex items-center justify-center gap-2"
                      >
                        <FaDownload size={14} /> Download
                      </a>
                    </>
                  )}
                  <button onClick={() => handleEdit(item)} className="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition"><FaEdit /></button>
                  <button onClick={() => handleDelete(item.id)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"><FaTrash /></button>
                </div>
              </div>
            </div>
          ))}
        </motion.div>

        {downloads.length === 0 && (
          <div className="text-center text-gray-600 mt-12 py-8">
            <p className="text-xl">No downloads found</p>
            <p className="text-sm mt-2">Click "Add Download" to create one</p>
          </div>
        )}

        {/* Modal Form */}
        {showForm && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">{editingId ? 'Edit' : 'Add'} Download</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="e.g., Academic Calendar 2026" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3" className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900 resize-none"
                    placeholder="Brief description of the file"></textarea>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900">
                      {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">File Type</label>
                    <select value={formData.fileType} onChange={(e) => setFormData({ ...formData, fileType: e.target.value })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900">
                      {fileTypes.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Upload File OR Provide URL</label>
                  <input type="file" onChange={handleFileChange} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.png"
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" />
                  {fileName && <p className="text-sm text-green-600 mt-2">✅ Selected: {fileName}</p>}
                  <input type="text" value={formData.fileUrl} onChange={(e) => setFormData({ ...formData, fileUrl: e.target.value })}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900 mt-2" 
                    placeholder="Or paste file URL here..." />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-5 h-5 text-blue-900 rounded focus:ring-blue-900" />
                  <label className="text-sm text-gray-700">Active</label>
                </div>
                <div className="flex gap-4 pt-4">
                  <button type="submit" className="flex-1 bg-blue-900 text-white py-3 rounded-lg hover:bg-blue-800 transition font-semibold">
                    {editingId ? 'Update' : 'Add'} Download
                  </button>
                  <button type="button" onClick={() => { setShowForm(false); resetForm(); }}
                    className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition font-semibold">Cancel</button>
                </div>
              </form>
            </div>
          </motion.div>
        )}
      </div>
    </AdminLayout>
  );
}

export default DownloadsManagement;
